/*
 * <product-tabs> — the Description / Ingredients / Reviews strip on the
 * product page.
 *
 * Implements the WAI-ARIA tabs pattern with automatic activation: arrow keys
 * move between tabs and open the one they land on, which is the expected
 * behaviour for tabs whose panels are already in the document.
 *
 * The markup ships with the first panel open and the rest carrying
 * `data-tab-inactive`, so the correct panel is painted on the first frame and
 * nothing reflows when this script upgrades the element.
 */
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        if (this.tabs.length === 0) return;

        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('keydown', this.handleKeydown);
      }

      handleClick = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.tabs.includes(tab)) this.select(tab);
      };

      handleKeydown = (event) => {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !this.tabs.includes(tab)) return;

        const last = this.tabs.length - 1;
        const current = this.tabs.indexOf(tab);
        let next;

        switch (event.key) {
          case 'ArrowRight':
            next = current === last ? 0 : current + 1;
            break;
          case 'ArrowLeft':
            next = current === 0 ? last : current - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }

        // Only now, once a handled key is confirmed, so Tab and the rest are
        // left to the browser.
        event.preventDefault();
        this.select(this.tabs[next]);
        this.tabs[next].focus();
      };

      select(selectedTab) {
        this.tabs.forEach((tab) => {
          const isSelected = tab === selectedTab;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));

          tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
          // Roving tabindex: only the open tab is a tab stop, so Tab moves out
          // of the strip and into the panel rather than across every tab.
          tab.setAttribute('tabindex', isSelected ? '0' : '-1');

          if (!panel) return;
          panel.toggleAttribute('data-tab-inactive', !isSelected);
        });
      }
    }
  );
}
